import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { admin, isApiError } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Button, Text } from '@repletoryreach/sdk-ui';
import { PageHeader } from '@repletoryreach/sdk-ui/data-display';
import { Switch, TextInput, Textarea } from '@repletoryreach/sdk-ui/inputs';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { useT } from '../../../i18n/I18nProvider';

/**
 * The company's details (owner's decision of 2 October 2026): the phone number, registration number
 * and street address the website, the terms and invoices show. Settings, not copy, so changing them
 * needs no release. A blank phone hides the line on the website.
 */
export default function ContentCompanyPage() {
  const t = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'site-company'], queryFn: () => admin.getSiteCompany() });
  const [legalName, setLegalName] = useState('');
  const [registration, setRegistration] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  // Whether the website shows each value (3 October 2026). Invoices carry them all regardless.
  const [showRegistration, setShowRegistration] = useState(true);
  const [showAddress, setShowAddress] = useState(true);
  const [showPhone, setShowPhone] = useState(true);
  const [seeded, setSeeded] = useState(false);
  const [phoneError, setPhoneError] = useState<string | undefined>();
  useEffect(() => {
    const d = q.data?.data;
    if (!d || seeded) return;
    setLegalName(d.legal_name); setRegistration(d.registration_number ?? ''); setAddress(d.address ?? ''); setPhone(d.phone ?? '');
    setShowRegistration(d.show_registration_number !== false); setShowAddress(d.show_address !== false); setShowPhone(d.show_phone !== false);
    setSeeded(true);
  }, [q.data, seeded]);

  const save = useMutation({
    mutationFn: () => admin.updateSiteCompany({
      legal_name: legalName, registration_number: registration, address, phone,
      show_registration_number: showRegistration, show_address: showAddress, show_phone: showPhone
    }),
    onMutate: () => setPhoneError(undefined),
    onSuccess: (res) => {
      qc.setQueryData(['admin', 'site-company'], res);
      setSeeded(false);
      notifySuccess(t('content.company.saved'), t('content.company.title'));
    },
    onError: (e) => {
      if (isApiError(e) && e.code === 'INVALID_PHONE') { setPhoneError(t('content.company.badPhone')); return; }
      notifyError(e instanceof Error ? e.message : t('common.unknownError'), t('content.company.title'));
    }
  });

  return (
    <Stack gap="lg">
      <PageHeader eyebrow={t('nav.section.content')} title={t('content.company.title')} description={t('content.company.description')} />
      <Card withBorder>
        <Stack gap="md">
          <TextInput label={t('content.company.legalName')} description={t('content.company.legalNameHint')} value={legalName} onChange={(e) => setLegalName(e.currentTarget.value)} maxLength={160} />
          <TextInput label={t('content.company.registration')} description={t('content.company.registrationHint')} value={registration} onChange={(e) => setRegistration(e.currentTarget.value)} placeholder="2021/123456/07" maxLength={40} />
          <Switch label={t('content.company.showOnSite')} checked={showRegistration} onChange={setShowRegistration} data-testid="show-registration" />
          <Textarea label={t('content.company.address')} description={t('content.company.addressHint')} value={address} onChange={(e) => setAddress(e.currentTarget.value)} minRows={2} maxLength={300} />
          <Switch label={t('content.company.showOnSite')} checked={showAddress} onChange={setShowAddress} data-testid="show-address" />
          <TextInput label={t('content.company.phone')} description={t('content.company.phoneHint')} value={phone} onChange={(e) => setPhone(e.currentTarget.value)} error={phoneError} placeholder="+27 82 000 0000" />
          <Switch label={t('content.company.showOnSite')} checked={showPhone} onChange={setShowPhone} data-testid="show-phone" />
          <Text role="caption" c="var(--rr-color-text-muted)">{t('content.company.showHint')}</Text>
          <Group justify="flex-end">
            <Button onClick={() => save.mutate()} loading={save.isPending} disabled={q.isPending}>{t('action.save')}</Button>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
